export const REVIEW_BONUS = 0.18;
export const ACTIVATION_THRESHOLD = 0.55;

export const constructSpecs = [
  {
    id: 'cog.representation.global_local_order',
    label: 'Global-local representation order',
    definition: 'Which representation is useful first: a whole-system frame or component sequence.',
    lowAnchor: 'components first',
    highAnchor: 'system frame first',
    alternatives: ['task familiarity', 'domain expertise', 'presentation wording'],
    mappings: {
      'uci.q01': { A: -1.0, B: 1.0, C: -0.4, D: 0.6 },
      'uci.q04': { A: -1.0, B: 1.0, C: 0.6, D: -0.2 },
      'uci.q19': { A: -1.0, B: 0.1, C: 0.2, D: 1.0 }
    }
  },
  {
    id: 'cog.processing.sequential_relational',
    label: 'Sequential-relational processing',
    category: 'cognition',
    definition: 'Whether an initial explanation is most useful as an ordered sequence or a relationship network.',
    lowAnchor: 'ordered sequence',
    highAnchor: 'relationship network',
    alternatives: ['problem type', 'domain experience', 'visual presentation'],
    mappings: {
      'uci.q01': { A: -1.0, B: 1.0, C: -0.7, D: 0.4 },
      'uci.q03': { A: -1.0, B: -0.4, C: 1.0, D: 0.5 },
      'uci.q08': { A: -0.9, B: -0.4, C: 1.0, D: -0.6 }
    }
  },
  {
    id: 'cog.filter.closure_threshold',
    label: 'Information closure threshold',
    category: 'cognition',
    definition: 'How much task-relevant evidence is useful before forming a working conclusion.',
    lowAnchor: 'early working closure',
    highAnchor: 'extended evidence search',
    alternatives: ['stakes', 'time pressure', 'topic familiarity'],
    mappings: {
      'uci.q02': { A: -1.0, B: 0.1, C: 0.6, D: 1.0 },
      'uci.q11': { A: -0.5, B: 0.7, C: -1.0, D: 1.0 },
      'uci.q12': { A: -1.0, B: -0.5, C: 0.3, D: 1.0 }
    }
  },
  {
    id: 'cog.reasoning.probabilistic',
    label: 'Uncertainty framing',
    category: 'cognition',
    definition: 'How uncertainty is most usefully represented when evidence is incomplete.',
    lowAnchor: 'single working answer',
    highAnchor: 'ranges, scenarios, and update conditions',
    alternatives: ['numeracy', 'risk', 'decision urgency'],
    mappings: {
      'uci.q05': { A: -1.0, B: 0.4, C: 1.0, D: 0.8 },
      'uci.q17': { A: 0.8, B: 0.5, C: 0.9, D: 1.0 }
    }
  },
  {
    id: 'cog.search.divergence_convergence',
    label: 'Search breadth before commitment',
    category: 'cognition',
    definition: 'How broadly to generate alternatives before selecting and refining a direction.',
    lowAnchor: 'converge quickly',
    highAnchor: 'diverge broadly',
    alternatives: ['deadline', 'solution familiarity', 'cost of exploration'],
    mappings: {
      'uci.q07': { A: -1.0, B: 0.2, C: -0.2, D: 1.0 }
    }
  },
  {
    id: 'motivation.improvement_target',
    label: 'Improvement target orientation',
    category: 'personality_motivation',
    definition: 'Whether improvement attention begins with personal execution or wider people, process, and possibility effects.',
    lowAnchor: 'personal execution',
    highAnchor: 'system or possibility',
    alternatives: ['current role', 'project stage', 'available authority'],
    mappings: {
      'uci.q06': { A: -1.0, B: 0.2, C: 0.7, D: 1.0 },
      'uci.q10': { A: -1.0, B: 0.2, C: 0.7, D: 1.0 }
    }
  },
  {
    id: 'emotion.logic_affect_integration',
    label: 'Logic-affect integration',
    category: 'emotion_self_regulation',
    definition: 'How emotional information is incorporated alongside factual reasoning during a decision.',
    lowAnchor: 'inspect logic separately',
    highAnchor: 'integrate logic and affect',
    alternatives: ['current emotional intensity', 'decision stakes', 'emotion vocabulary'],
    mappings: {
      'uci.q09': { A: -1.0, B: 0.6, C: 1.0, D: 0.2 }
    }
  },
  {
    id: 'pref.decision.pace',
    label: 'Decision pace',
    category: 'collaboration_preference',
    definition: 'The useful cadence for moving from options to a reversible decision.',
    lowAnchor: 'rapid iteration',
    highAnchor: 'deliberate evaluation',
    alternatives: ['stakes', 'reversibility', 'fatigue'],
    mappings: {
      'uci.q12': { A: -1.0, B: -0.5, C: 0.3, D: 1.0 }
    }
  },
  {
    id: 'cog.explanation.representation',
    label: 'Explanation representation',
    category: 'collaboration_preference',
    definition: 'Which representation makes an accurate explanation usable fastest.',
    lowAnchor: 'concrete verbal sequence',
    highAnchor: 'relational or visual frame',
    alternatives: ['topic', 'audience', 'device', 'accessibility needs'],
    mappings: {
      'uci.q13': { A: -1.0, B: 0.2, C: 1.0, D: 0.5 },
      'uci.q23': { A: 0.1, B: -1.0, C: 1.0, D: 0.3 }
    }
  },
  {
    id: 'social.conflict_orientation',
    label: 'Disagreement navigation',
    category: 'social_interaction',
    definition: 'Which move is most useful first when a disagreement must become a decision.',
    lowAnchor: 'perspective and shared criteria',
    highAnchor: 'synthesis and direct testing',
    alternatives: ['power', 'relationship', 'psychological safety'],
    mappings: {
      'uci.q14': { A: -1.0, B: -0.4, C: 0.6, D: 1.0 }
    }
  },
  {
    id: 'cog.memory.reconstruction_cue',
    label: 'Memory reconstruction cue',
    category: 'cognition',
    definition: 'Which evidence channel is most useful first when reconstructing a past event.',
    lowAnchor: 'external records',
    highAnchor: 'experiential cues',
    alternatives: ['record availability', 'time since event', 'event salience'],
    mappings: {
      'uci.q15': { A: -1.0, B: 0.7, C: 0.1, D: 1.0 }
    }
  },
  {
    id: 'pref.work.flow_environment',
    label: 'Flow support environment',
    category: 'collaboration_preference',
    definition: 'Which structure and time signals are most likely to support sustained engagement.',
    lowAnchor: 'external structure and check-ins',
    highAnchor: 'autonomy and immersion',
    alternatives: ['task type', 'energy', 'attention state', 'deadline'],
    mappings: {
      'uci.q16': { A: -0.8, B: 0.6, C: -1.0, D: 1.0 },
      'uci.q20': { A: -1.0, B: 1.0, C: 0.2, D: 0.0 }
    }
  },
  {
    id: 'social.group_role',
    label: 'Collaboration role signal',
    category: 'social_interaction',
    definition: 'Which information and contribution style becomes useful first in group work.',
    lowAnchor: 'coordination and connection',
    highAnchor: 'analysis and novel direction',
    alternatives: ['team composition', 'formal role', 'status', 'expertise'],
    mappings: {
      'uci.q18': { A: -0.6, B: 0.5, C: -1.0, D: 0.7 },
      'uci.q22': { A: -0.8, B: 0.6, C: -1.0, D: 1.0 }
    }
  },
  {
    id: 'cog.metacognition.strategy_revision',
    label: 'Strategy revision',
    definition: 'Detection of a failing approach and deliberate selection of a different reasoning strategy.',
    lowAnchor: 'persist with the initial strategy',
    highAnchor: 'detect and revise the strategy',
    alternatives: ['self-image', 'feedback safety', 'available alternatives'],
    mappings: {
      'uci.q21': { A: 0.5, B: -0.6, C: 0.1, D: 1.0 },
      'uci.q25': { A: 0.8, B: 0.4, C: 0.3, D: 1.0 }
    }
  },
  {
    id: 'emotion.reset_signal',
    label: 'Reset signal',
    category: 'emotion_self_regulation',
    definition: 'Which signal most reliably indicates that a working reset would help.',
    lowAnchor: 'attention or performance cue',
    highAnchor: 'body or emotional cue',
    alternatives: ['sleep', 'health', 'task demand', 'environment'],
    mappings: {
      'uci.q24': { A: -0.6, B: 0.7, C: -1.0, D: 1.0 }
    }
  }
];

function answerFor(state, questionId) {
  return state.answers[questionId] || { selected: [], ownWords: '' };
}

export function rawEvidence(instrument, state) {
  return instrument.questions.map(question => {
    const answer = answerFor(state, question.id);
    return {
      questionId: question.id,
      question: question.prompt,
      options: question.options.map(option => ({ id: option.id, text: option.text })),
      selected: answer.selected.slice(),
      selectedText: question.options.filter(option => answer.selected.includes(option.id)),
      ownWords: answer.ownWords.trim()
    };
  });
}

export function estimateConstruct(instrument, state, spec) {
  const evidence = [];
  const numericSignals = [];
  let ownWordsCount = 0;

  Object.entries(spec.mappings).forEach(([questionId, mapping]) => {
    const answer = answerFor(state, questionId);
    const selectedValues = answer.selected
      .filter(choice => mapping[choice] !== undefined)
      .map(choice => mapping[choice]);
    if (selectedValues.length) {
      numericSignals.push(selectedValues.reduce((a, b) => a + b, 0) / selectedValues.length);
    }
    if (answer.ownWords.trim()) ownWordsCount += 1;
    evidence.push({
      evidenceId: `${spec.id}.${questionId}`,
      sourceType: 'self_report',
      sourceRef: questionId,
      observation: `Selected ${answer.selected.join(', ') || 'none'}.${answer.ownWords.trim() ? ` Own words: ${answer.ownWords.trim()}` : ''}`,
      interpretation: selectedValues.length
        ? `Bounded responses provide a weak directional signal for ${spec.label.toLowerCase()}.`
        : 'Own-words evidence is preserved but not automatically scored.',
      weight: answer.ownWords.trim() ? 0.4 : 0.25
    });
  });

  const score = numericSignals.length
    ? numericSignals.reduce((a, b) => a + b, 0) / numericSignals.length
    : 0;
  let confidence = (numericSignals.length / Object.keys(spec.mappings).length) * 0.4;
  confidence += (ownWordsCount / Object.keys(spec.mappings).length) * 0.08;
  const review = state.reviews[spec.id];
  if (review === 'accurate') confidence = Math.min(0.7, confidence + REVIEW_BONUS);
  if (review === 'correction') confidence = Math.min(confidence, 0.15);

  if (review) {
    evidence.push({
      evidenceId: `${spec.id}.user-review`,
      sourceType: 'correction',
      sourceRef: 'wizard.construct-review',
      observation: review === 'accurate'
        ? 'User marked the hypothesis accurate enough to trial.'
        : 'User marked the hypothesis as needing correction.',
      interpretation: review === 'accurate'
        ? 'Raises product-routing confidence only; it does not validate the construct.'
        : 'Defer adaptation until corrected evidence is captured.',
      weight: 0.25
    });
  }

  return {
    constructId: spec.id,
    category: spec.category || 'cognition',
    operationalDefinition: spec.definition,
    estimate: {
      score: Number(score.toFixed(3)),
      confidence: Number(confidence.toFixed(3)),
      lowAnchor: spec.lowAnchor,
      highAnchor: spec.highAnchor
    },
    signalCount: numericSignals.length,
    evidence,
    alternativeExplanations: spec.alternatives.slice(),
    untested: true
  };
}

export function buildModel(instrument, state, options = {}) {
  const constructs = constructSpecs
    .map(spec => estimateConstruct(instrument, state, spec))
    .filter(construct => construct.signalCount > 0);
  return {
    modelVersion: instrument.modelVersion || '0.2',
    status: 'hypothesis',
    subjectId: options.subjectId || 'local-preview',
    generatedAt: options.generatedAt || new Date().toISOString(),
    constructs,
    limitations: [
      'Reconstructed scenario wording and a small self-report evidence set.',
      'Every structured question contributes to at least one candidate construct.',
      'Own-words responses are preserved verbatim but are not automatically interpreted in v0.1.',
      'User confirmation changes trial eligibility, not scientific validity.'
    ],
    provenance: {
      instrument: instrument.instrumentId,
      instrumentVersion: instrument.instrumentVersion,
      sourceRevision: instrument.provenance.sourceRevision,
      notes: 'Reconstructed 25-item instrument; full structured-response coverage across candidate constructs.'
    }
  };
}

function makeRule(state, ruleId, construct, mode, behavior, guardrail) {
  const review = state.reviews[construct.constructId];
  const userDisabled = state.ruleOverrides?.[ruleId] === 'disabled';
  const applied = !userDisabled && construct.estimate.confidence >= ACTIVATION_THRESHOLD && review === 'accurate';
  return {
    ruleId,
    constructId: construct.constructId,
    mode,
    status: userDisabled ? 'disabled' : applied ? 'applied' : 'deferred',
    behavior,
    guardrail,
    reason: userDisabled
      ? 'user_disabled'
      : applied
      ? 'user_reviewed_hypothesis_above_trial_threshold'
      : review === 'correction' ? 'user_correction_required' : 'low_confidence_or_not_reviewed',
    basis: { score: construct.estimate.score, confidence: construct.estimate.confidence }
  };
}

export function buildAdapterPlan(model, state) {
  const globalOrder = model.constructs.find(item => item.constructId === 'cog.representation.global_local_order');
  const revision = model.constructs.find(item => item.constructId === 'cog.metacognition.strategy_revision');
  const globalFirst = globalOrder?.estimate.score >= 0.2;
  const componentFirst = globalOrder?.estimate.score <= -0.2;
  const start = globalFirst
    ? 'Present the end-state or system map before the component sequence.'
    : componentFirst
      ? 'Present the component sequence before the system-level synthesis.'
      : 'Present a compact system frame and component sequence together.';
  const complement = globalFirst
    ? 'Follow the system frame with a concise dependency checklist.'
    : componentFirst
      ? 'Follow the component sequence with a system map showing relationships.'
      : 'Offer the alternate representation when the first explanation does not land.';

  const rules = [];
  if (globalOrder) {
    rules.push(
      makeRule(state, 'mirror.representation-order', globalOrder, 'mirror', start, 'Do not omit dependencies or material uncertainty.'),
      makeRule(state, 'complement.alternate-representation', globalOrder, 'complement', complement, 'Avoid duplicating the whole answer.'),
      makeRule(state, 'counterbalance.dependency-audit', globalOrder, 'counterbalance', 'Before a high-stakes commitment, check what the preferred representation may hide.', 'Trigger only when stakes or task evidence justify it.')
    );
  }
  if (revision) {
    rules.push(
      makeRule(state, 'counterbalance.strategy-revision', revision, 'counterbalance', 'After repeated failure, name the current assumption and test one alternate strategy.', 'Do not challenge reflexively; require failure evidence.')
    );
  }

  return {
    adapterVersion: '0.1',
    status: 'hypothesis',
    activationThreshold: ACTIVATION_THRESHOLD,
    rules,
    untouchedLayers: ['approvalDiet', 'protocols']
  };
}

const directivePriority = [
  'uci.q01', 'uci.q05', 'uci.q12', 'uci.q20', 'uci.q03', 'uci.q25',
  'uci.q02', 'uci.q06', 'uci.q07', 'uci.q19', 'uci.q21', 'uci.q22',
  'uci.q18', 'uci.q15', 'uci.q13', 'uci.q23'
];

const directiveContexts = {
  'uci.q01': 'When orienting in an unfamiliar project',
  'uci.q02': 'When sorting mixed-quality information',
  'uci.q03': 'When diagnosing a recurring problem',
  'uci.q05': 'When evidence is incomplete',
  'uci.q06': 'When planning an improvement',
  'uci.q07': 'When creating a first direction',
  'uci.q12': 'For a low-stakes reversible decision',
  'uci.q13': 'When an explanation is not landing',
  'uci.q15': 'When reconstructing a past meeting',
  'uci.q18': 'When learning how to work with a new collaborator',
  'uci.q19': 'When diagnosing a failing launch',
  'uci.q20': 'During a long AI-assisted work session',
  'uci.q21': 'When concrete evidence conflicts with the current approach',
  'uci.q22': 'When a group is stuck',
  'uci.q23': 'When an accurate concept still feels abstract',
  'uci.q25': 'After an approach has failed twice'
};

const canonicalOwnWordDirectives = {
  'uci.q01': [/(end goal|everything involved|more data)/i, 'Start with the intended end state and a map of the whole system; then identify every component and dependency that can affect the outcome.'],
  'uci.q02': [/(filter out lower quality|tiers? on priority|analy[sz]e, compare)/i, 'Analyze all relevant sources together, compare them, find connections, filter out lower-quality material, rank the remainder by quality and priority, and then recommend a decision.'],
  'uci.q03': [/(bug screen|broken link|reverse engineer)/i, 'Run a bug screen first, locate and repair the broken link, and report what failed and what changed. If that does not resolve it, reverse-engineer from the intended result and change strategies.'],
  'uci.q05': [/(false reports|must be factual|honest of the limitations)/i, 'Never invent certainty or information just to provide an answer. Separate facts from assumptions, state the limitations plainly, and identify what evidence could change the conclusion.'],
  'uci.q06': [/(current workflow|inefficien|flaws)/i, 'Begin improvement work by auditing the current workflow for bottlenecks, inefficiencies, and defects before proposing changes.'],
  'uci.q07': [/(all viable options|combine a few|hone in)/i, 'Explore every viable direction that fits the available information, combine compatible strengths, and then converge on the strongest practical choice.'],
  'uci.q12': [/(instant decisions|best, most educated|first do C)/i, 'For reversible, low-stakes choices, compare the few likely scenarios quickly, make the best-supported decision immediately, and adjust after testing it.'],
  'uci.q13': [/(bad at this|not good at this)/i, 'If an explanation does not land, switch representations: use a familiar analogy first, then reduce it to a short sequence of concrete steps.'],
  'uci.q15': [/\bA\b.*\bC\b.*\bB\b.*\bD\b/i, 'Reconstruct past events in this order: written records and decisions, the event sequence, emotional context, then sensory details.'],
  'uci.q18': [/(\bB\b.*\bD\b|ask people how they work)/i, 'Ask collaborators directly how they work best; verify that against their problem-solving behavior, role, incentives, and team context.'],
  'uci.q19': [/\bA\b.*\bD\b.*\bC\b/i, 'Diagnose a failing launch in this order: concrete metrics and failure points, the wider market/product/operating system, then the human handoffs and decisions.'],
  'uci.q20': [/(timelines ai puts|how long things used to take|ai actually moves)/i, 'Track progress from the live AI-assisted workflow. Do not estimate delivery from pre-AI timelines or interrupt with arbitrary time checks.'],
  'uci.q21': [/(test it against|current approach)/i, 'Test the conflicting result directly against the current approach, inspect the exact mismatch, and change direction only after identifying what assumption failed.'],
  'uci.q22': [/(pain point|bottleneck|brainstorming ideas)/i, 'Find the group’s actual bottleneck first, then propose and test solution directions that remove it.'],
  'uci.q23': [/(mix of all|all of the above)/i, 'Use a relationship diagram first; add a familiar analogy, practical component breakdown, or realistic example until the concept becomes usable.'],
  'uci.q25': [/(new\/different way|others failed|\bC\b.*\bA\b)/i, 'After two failed attempts, use the failure evidence to choose a different method. Step back briefly if needed, then run a small test of the new approach instead of repeating the old one.']
};

function selectedOptionsInOrder(item) {
  const optionMap = new Map((item.options || []).map(option => [option.id, option.text]));
  const mentioned = [...String(item.ownWords || '').matchAll(/\b([A-D])\b/g)].map(match => match[1]);
  const ids = mentioned.length > 1 ? mentioned : (item.selected || []).filter(id => id !== 'E');
  return ids.map(id => optionMap.get(id)).filter(Boolean);
}

function fallbackDirective(item) {
  const ownWords = String(item.ownWords || '').trim();
  const context = directiveContexts[item.questionId];
  if (!context || !ownWords) return null;
  if (/(admittedly bad|kind of a mix|all of the above)/i.test(ownWords)) {
    const choices = selectedOptionsInOrder(item);
    return choices.length ? `${context}, use these approaches in order: ${choices.join(' Then ')}` : null;
  }
  const choices = selectedOptionsInOrder(item);
  if (/\b[A-D]\b/.test(ownWords) && choices.length > 1) {
    return `${context}, use this order: ${choices.join(' Then ')}`;
  }
  const cleaned = ownWords
    .replace(/^i(?:'d| would)\s+/i, '')
    .replace(/^ai should\s+/i, '')
    .replace(/^should\s+/i, '')
    .replace(/^to\s+/i, '')
    .replace(/\s+/g, ' ');
  return `${context}, follow this user direction: ${cleaned}`;
}

function directivesFromEvidence(evidence = []) {
  const byId = new Map(evidence.map(item => [item.questionId, item]));
  return directivePriority.flatMap(questionId => {
    const item = byId.get(questionId);
    if (!item?.ownWords?.trim()) return [];
    const canonical = canonicalOwnWordDirectives[questionId];
    const instruction = canonical?.[0].test(item.ownWords) ? canonical[1] : fallbackDirective(item);
    return instruction ? [{
      id: `directive.${questionId}`,
      status: 'active',
      source: 'user_authored_response',
      instruction
    }] : [];
  });
}

function formatAgentInstructions(directives) {
  const lines = [
    '# 3Fold AI Workstyle',
    '',
    'Use this profile as active working instructions for collaborating with this user.',
    '',
    'Operating rules:',
    '- Follow the directives below as active working preferences.',
    '- Treat them as reversible defaults, not permanent labels.',
    '- When the user corrects a preference, follow the correction and update your working assumption.',
    '- Do not mention scores, confidence, assessment mechanics, or this file unless the user asks.',
    '- These instructions change how you communicate and execute; they do not reduce rigor, accuracy, or necessary safety checks.'
  ];

  if (directives.length) lines.push('', 'Directives:');
  directives.forEach(item => lines.push(`- ${item.instruction}`));

  return lines.join('\n');
}

export function buildAgentSetup(model, adapterPlan, evidence) {
  const userDirectives = directivesFromEvidence(evidence);
  const ruleDefaults = (adapterPlan.rules || [])
    .filter(rule => rule.status !== 'disabled')
    .map(rule => ({
      id: rule.ruleId,
      status: rule.status === 'applied' ? 'confirmed' : 'active_default',
      source: 'assessment_pattern',
      mode: rule.mode,
      instruction: rule.behavior,
      guardrail: rule.guardrail
    }));
  const normalizedUserText = userDirectives.map(item => item.instruction.toLowerCase()).join(' ');
  const nonDuplicativeDefaults = ruleDefaults.filter(item => {
    if (item.id === 'mirror.representation-order') return !/end state|system/.test(normalizedUserText);
    if (item.id === 'complement.alternate-representation') return !/dependenc/.test(normalizedUserText);
    if (item.id === 'counterbalance.strategy-revision') return !/failed attempts|change strategies|different method/.test(normalizedUserText);
    return true;
  });
  const directives = [...userDirectives, ...nonDuplicativeDefaults];

  return {
    profileVersion: '1.1',
    status: directives.length ? 'ready' : 'empty',
    purpose: 'Portable instructions that tell an AI how to communicate and work with this user.',
    directives,
    installText: formatAgentInstructions(directives)
  };
}

function stateFromPackage(pkg) {
  const answers = Object.fromEntries((pkg.rawEvidence || []).map(item => [item.questionId, {
    selected: Array.isArray(item.selected) ? item.selected.slice() : [],
    ownWords: String(item.ownWords || '')
  }]));
  const reviews = {};
  (pkg.cognitiveModel?.constructs || []).forEach(construct => {
    const review = (construct.evidence || []).find(item => item.sourceRef === 'wizard.construct-review');
    if (!review) return;
    reviews[construct.constructId] = review.observation?.startsWith('User marked the hypothesis accurate')
      ? 'accurate'
      : 'correction';
  });
  return { answers, reviews, ruleOverrides: {} };
}

export function ensureAgentSetup(instrument, pkg) {
  if (!pkg) return pkg;
  if (pkg.agentSetup?.profileVersion === '1.1' && pkg.agentSetup?.installText) return pkg;
  const state = stateFromPackage(pkg);
  const rebuiltEvidence = rawEvidence(instrument, state);
  const evidence = rebuiltEvidence.map(item => ({
    ...item,
    ...(pkg.rawEvidence || []).find(saved => saved.questionId === item.questionId),
    options: item.options
  }));
  return {
    ...pkg,
    exportVersion: '0.4',
    agentSetup: buildAgentSetup(pkg.cognitiveModel || {}, pkg.adapterPlan || { rules: [] }, evidence),
    rawEvidence: evidence
  };
}

export function portableAgentPackage(pkg) {
  if (!pkg?.agentSetup) return null;
  return {
    format: '3fold.ai-workstyle',
    version: pkg.agentSetup.profileVersion,
    generatedAt: pkg.exportedAt,
    status: pkg.agentSetup.status,
    purpose: pkg.agentSetup.purpose,
    operatingRules: [
      'Follow these directives as active working preferences.',
      'Treat them as reversible defaults and update them when the user corrects you.',
      'Do not narrate assessment scores or mechanics unless the user asks.',
      'Do not trade away rigor, accuracy, or necessary safety checks.'
    ],
    directives: pkg.agentSetup.directives.map(item => item.instruction)
  };
}

export function buildEvidencePackage(instrument, state, options = {}) {
  const cognitiveModel = buildModel(instrument, state, options);
  const evidence = rawEvidence(instrument, state);
  const adapterPlan = buildAdapterPlan(cognitiveModel, state);
  return {
    exportVersion: '0.4',
    exportedAt: options.generatedAt || new Date().toISOString(),
    agentSetup: buildAgentSetup(cognitiveModel, adapterPlan, evidence),
    cognitiveModel,
    adapterPlan,
    rawEvidence: evidence,
    collaborationPolicy: {
      approvalDiet: {
        version: '0.1',
        status: 'separate_unconfigured',
        inferredFromCognition: false,
        rules: []
      },
      protocols: {
        version: '0.1',
        status: 'separate_unconfigured',
        inferredFromCognition: false,
        active: []
      }
    }
  };
}
